import { Pagination, Spin } from 'antd';
import { useAtom, useAtomValue } from 'jotai';
import { searchPageNoAtom, searchPageSizeAtom } from 'packages/omnitag/state';
import {
  allSearchResultsAtom,
  filteredSearchResultsTotalCountAtom,
  searchQueryAtom,
} from 'packages/omnitag/state/text-search';
import { useHistory, useLocation } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { changeURLParams } from 'packages/lib/utils/url';

export const ResultsPagination = (): JSX.Element | null => {
  const history = useHistory();
  const location = useLocation();
  const searchQuery = useAtomValue(searchQueryAtom);
  const searchResults = useAtomValue(allSearchResultsAtom);
  const totalResults = useAtomValue(filteredSearchResultsTotalCountAtom);
  const [pageSize, setPageSize] = useAtom(searchPageSizeAtom);
  const [pageNo, setPageNo] = useAtom(searchPageNoAtom);

  const onChange = useCallback(
    (pageNo: number, pageSize: number) => {
      setPageNo(pageNo);
      setPageSize(pageSize);

      changeURLParams(history, location, {
        pageSize: String(pageSize),
        pageNo: String(pageNo),
      });
    },
    [history, location, setPageNo, setPageSize]
  );

  // Corner case: ensure pageNo is within bounds. (User could go to last page then increase pageSize, resulting in pageNo being out of bounds)
  useEffect(() => {
    if (searchResults && searchResults.data) {
      if (pageNo * pageSize > totalResults || pageNo < 1) {
        let newPageNo = Math.floor((totalResults - 1) / pageSize) + 1;
        if (newPageNo < 1) newPageNo = 1;
        if (pageNo !== newPageNo) {
          onChange(newPageNo, pageSize);
        }
      }
    }
  }, [searchResults, totalResults, pageNo, pageSize, onChange]);

  const searchResultsAreValid =
    searchResults && !searchResults.error && !searchResults.isLoading;

  return (
    <>
      {searchResults && searchResults.isLoading && <Spin />}
      {searchResultsAreValid && (searchQuery?.text ?? '').length > 0 && (
        <Pagination
          onChange={onChange}
          total={totalResults}
          showTotal={(total, [n, m]): string => {
            if (total <= 0) {
              return 'No items.';
            }
            if (n <= 0 || m <= 0) {
              return `${total} items.`;
            }
            return `${n}-${m} of ${total} items`;
          }}
          showSizeChanger
          showQuickJumper
          pageSizeOptions={[20, 48, 100, 200]}
          current={pageNo}
          pageSize={pageSize}
        />
      )}
    </>
  );
};
